import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface Input {
  id: string;
  inputCode: string;
  inputName: string;
  description: string;
  inputTypeId: string;
  inputTypeCode: string;
  inputTypeName: string;
  supplierId: string;
  supplierProductCode: string;
  unitOfMeasure: string;
  unitOfMeasureDisplay: string;
  unitCost: number;
  hasExpiryDate: boolean;
  shelfLifeDays: number;
  safetyInstructions: string;
  storageRequirements: string;
  /** Crop variety surfaced per spec §b/§f. Required at receive time when the
   * input's InputType is seed-related; non-seed inputs may omit it. */
  cropTypeId?: string;
  variety?: string;
  currentStock?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateInputRequest {
  inputCode?: string;
  inputName: string;
  description: string;
  inputTypeId: string;
  supplierId?: string;
  supplierProductCode: string;
  unitOfMeasure: string;
  unitCost: number;
  hasExpiryDate: boolean;
  shelfLifeDays: number;
  safetyInstructions: string;
  storageRequirements: string;
  cropTypeId?: string;
  variety?: string;
}

export interface UpdateInputRequest extends CreateInputRequest {
  isActive: boolean;
}

export const inputsApi = {
  // For the management table — returns a single page with metadata
  getInputsPaged: async (
    pageNumber = 1,
    pageSize = 20,
    search?: string,
    supplierId?: string
  ): Promise<PagedResult<Input>> => {
    const params = new URLSearchParams();
    params.set('pageNumber', String(pageNumber));
    params.set('pageSize', String(pageSize));
    if (search) params.set('search', search);
    if (supplierId) params.set('supplierId', supplierId);
    const { data } = await apiClient.get<ApiResponse<PagedResult<Input>>>(
      `/Inputs?${params.toString()}`
    );
    return data.data;
  },

  getInputById: async (id: string): Promise<Input> => {
    const { data } = await apiClient.get<ApiResponse<Input>>(
      `/Inputs/${id}`
    );
    return data.data;
  },

  getLowStockInputs: async (): Promise<Input[]> => {
    const { data } = await apiClient.get<ApiResponse<Input[]>>(
      '/Inputs/low-stock'
    );
    return data.data;
  },

  createInput: async (payload: CreateInputRequest): Promise<Input> => {
    const body = {
      ...payload,
      unitOfMeasure:
        payload.unitOfMeasure !== undefined && payload.unitOfMeasure !== null
          ? Number(payload.unitOfMeasure)
          : payload.unitOfMeasure,
    };

    const { data } = await apiClient.post<ApiResponse<Input>>('/Inputs', body);
    return data.data;
  },

  updateInput: async (id: string, payload: UpdateInputRequest): Promise<Input> => {
    const { data } = await apiClient.put<ApiResponse<Input>>(
      `/Inputs/${id}`,
      payload
    );
    return data.data;
  },

  deleteInput: async (id: string): Promise<void> => {
    await apiClient.delete(`/Inputs/${id}`);
  },
};
